import React, { useEffect, useState } from "react";
import {
  CheckCircle2, ChevronDown, Camera, AlertTriangle, Zap, Ban, ShieldAlert, Info,
  Thermometer, Scale, CalendarDays, ListChecks, Type, Hash, ImageIcon, Loader2,
} from "lucide-react";
import api, { fetchBlobUrl } from "../lib/api";
import { Badge, Button, Card } from "../lib/ui";
import { useToast } from "../lib/toast";

/* Kitchen SOP Engine (Phase 2B) — execution checklist rendered as accordion sections
   inside Meja Prepare. Photos reuse POST /api/prepare/photo (existing storage helper). */

const TYPE_ICONS = {
  checkbox: ListChecks, number: Hash, temperature: Thermometer, weight: Scale,
  date: CalendarDays, dropdown: ListChecks, text: Type, photo: ImageIcon,
};
const SEV_META = {
  info: { label: "INFO", cls: "bg-blue-50 text-blue-700" },
  warning: { label: "WARNING", cls: "bg-amber-50 text-amber-700" },
  critical: { label: "CRITICAL", cls: "bg-red-50 text-red-700" },
};

function SopPhoto({ path, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true, obj = null;
    if (path) fetchBlobUrl(`/prepare/photo/${path}`).then((u) => { if (live) { obj = u; setUrl(u); } }).catch(() => {});
    return () => { live = false; if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  return url ? <img src={url} alt="sop" className={className} /> : <div className={`${className} animate-pulse bg-stone-100`} />;
}

function PhotoPicker({ photos, setPhotos, testid }) {
  const [uploading, setUploading] = useState(false);
  const toast = useToast();
  const pick = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/prepare/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.path]);
    } catch { toast.error("Gagal mengunggah foto"); } finally { setUploading(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {photos.map((p, i) => (
        <div key={p} className="relative h-12 w-12 overflow-hidden rounded-lg border border-line">
          <SopPhoto path={p} className="h-full w-full object-cover" />
          <button type="button" onClick={() => setPhotos((a) => a.filter((_, j) => j !== i))}
            className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-bl bg-black/60 text-[9px] text-white">×</button>
        </div>
      ))}
      <label className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-line text-ink-mute hover:border-primary" data-testid={testid}>
        {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
    </div>
  );
}

/* ------------------------------- single item row ------------------------------- */
function SopItemRow({ item, tplKey, whId, canCreate, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(item.answer?.value ?? "");
  const [note, setNote] = useState(item.answer?.note || "");
  const [photos, setPhotos] = useState(item.answer?.photos || []);

  const ans = item.answer;
  const isAuto = !!item.auto_rule;
  const passed = item.passed;
  const needsDetail = item.require_note !== "disabled" || item.require_photo !== "disabled"
    || ["photo", "text"].includes(item.check_type);
  const TypeIcon = TYPE_ICONS[item.check_type] || ListChecks;
  const sev = SEV_META[item.severity] || SEV_META.info;

  const submit = async (val) => {
    setBusy(true);
    try {
      await api.post("/prepare/sop/answer", {
        warehouse: whId || undefined, template_key: tplKey, item_id: item.item_id,
        value: val, note: note || null, photos,
      });
      setOpen(false);
      onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Gagal"));
    } finally { setBusy(false); }
  };

  const clearAnswer = async () => {
    setBusy(true);
    try {
      await api.post("/prepare/sop/answer", { warehouse: whId || undefined, template_key: tplKey, item_id: item.item_id, value: null });
      setValue(""); setNote(""); setPhotos([]);
      onChanged();
    } catch { toast.error("Gagal"); } finally { setBusy(false); }
  };

  const statusIcon = passed
    ? <CheckCircle2 size={18} className="text-emerald-600" />
    : ans && !passed
      ? <AlertTriangle size={18} className="text-red-600" />
      : <span className="block h-[18px] w-[18px] rounded-full border-2 border-stone-300" />;

  const quickToggle = item.check_type === "checkbox" && !needsDetail;

  return (
    <div className={`rounded-xl border p-2.5 ${passed ? "border-emerald-200 bg-emerald-50/30" : ans ? "border-red-200 bg-red-50/30" : "border-line bg-white"}`}
      data-testid={`sop-item-${item.item_id}`}>
      <div className="flex items-start gap-2.5">
        {isAuto ? (
          <span className="mt-0.5 shrink-0">{passed ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Zap size={18} className="text-amber-500" />}</span>
        ) : quickToggle && canCreate ? (
          <button type="button" disabled={busy} onClick={() => (ans ? clearAnswer() : submit(true))}
            className="mt-0.5 shrink-0" data-testid={`sop-toggle-${item.item_id}`}>{statusIcon}</button>
        ) : (
          <span className="mt-0.5 shrink-0">{statusIcon}</span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className={`text-sm font-bold ${passed ? "text-emerald-800" : "text-ink"}`}>{item.label}</p>
            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-extrabold ${sev.cls}`}>{sev.label}</span>
            {item.block === "block" && <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-extrabold text-red-700"><Ban size={9} /> WAJIB</span>}
            {item.block === "warn" && <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-extrabold text-amber-700"><ShieldAlert size={9} /> WARN</span>}
            {isAuto && <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-extrabold text-violet-700"><Zap size={9} /> OTOMATIS</span>}
            {item.branch_added && <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-extrabold text-sky-700">CABANG</span>}
          </div>
          {item.hint && <p className="text-[11px] text-ink-mute">{item.hint}</p>}
          {isAuto && <p className={`text-[11px] ${passed ? "text-emerald-700" : "text-amber-700"}`} data-testid={`sop-auto-detail-${item.item_id}`}>{item.auto_detail}</p>}
          {ans && !isAuto && (
            <p className="text-[11px] text-ink-soft">
              {item.check_type !== "checkbox" && <>Nilai: <b className={passed ? "text-emerald-700" : "text-red-700"}>{String(ans.value)}</b> · </>}
              {ans.checked_by_name} · {(ans.checked_at || "").slice(11, 16)}
              {ans.note && <> · “{ans.note}”</>}
            </p>
          )}
          {ans?.photos?.length > 0 && (
            <div className="mt-1 flex gap-1.5">
              {ans.photos.map((p) => <SopPhoto key={p} path={p} className="h-10 w-10 rounded-lg object-cover" />)}
            </div>
          )}
          {/* input area (manual, non-quick items) */}
          {canCreate && !isAuto && !quickToggle && (open || !ans) && (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {item.check_type === "checkbox" && (
                  <label className="flex items-center gap-1.5 text-xs font-bold text-ink">
                    <input type="checkbox" checked={value === true} onChange={(e) => setValue(e.target.checked)} data-testid={`sop-check-${item.item_id}`} />
                    Selesai / Sesuai
                  </label>
                )}
                {["number", "temperature", "weight"].includes(item.check_type) && (
                  <div className="flex items-center gap-1.5">
                    <TypeIcon size={14} className="text-ink-mute" />
                    <input type="number" step="0.1" value={value} onChange={(e) => setValue(e.target.value)}
                      placeholder="0" data-testid={`sop-num-${item.item_id}`}
                      className="w-24 rounded-lg border border-line px-2 py-1.5 text-sm font-bold focus:border-primary focus:outline-none" />
                    <span className="text-xs text-ink-mute">{item.check_type === "temperature" ? "°C" : (item.validation?.unit || "")}</span>
                  </div>
                )}
                {item.check_type === "date" && (
                  <input type="date" value={value} onChange={(e) => setValue(e.target.value)} data-testid={`sop-date-${item.item_id}`}
                    className="rounded-lg border border-line px-2 py-1.5 text-sm focus:border-primary focus:outline-none" />
                )}
                {item.check_type === "dropdown" && (
                  <select value={value} onChange={(e) => setValue(e.target.value)} data-testid={`sop-dd-${item.item_id}`}
                    className="rounded-lg border border-line bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none">
                    <option value="">— pilih —</option>
                    {(item.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                )}
                {item.check_type === "text" && (
                  <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Isi jawaban…" data-testid={`sop-text-${item.item_id}`}
                    className="w-full max-w-xs rounded-lg border border-line px-2 py-1.5 text-sm focus:border-primary focus:outline-none" />
                )}
              </div>
              {item.require_note !== "disabled" && (
                <input value={note} onChange={(e) => setNote(e.target.value)} data-testid={`sop-note-${item.item_id}`}
                  placeholder={item.require_note === "required" ? "Catatan (wajib)" : "Catatan (opsional)"}
                  className="w-full rounded-lg border border-line px-2 py-1.5 text-xs focus:border-primary focus:outline-none" />
              )}
              {(item.require_photo !== "disabled" || item.check_type === "photo") && (
                <PhotoPicker photos={photos} setPhotos={setPhotos} testid={`sop-photo-${item.item_id}`} />
              )}
              <Button size="sm" loading={busy}
                disabled={item.check_type === "photo" ? photos.length === 0 : (item.check_type === "checkbox" ? value !== true : String(value).trim() === "")}
                onClick={() => submit(item.check_type === "photo" ? true : (item.check_type === "checkbox" ? true : value))}
                data-testid={`sop-save-${item.item_id}`}>
                <CheckCircle2 size={13} /> Simpan
              </Button>
            </div>
          )}
        </div>
        {canCreate && !isAuto && !quickToggle && ans && !open && (
          <button onClick={() => { setValue(ans.value ?? ""); setNote(ans.note || ""); setPhotos(ans.photos || []); setOpen(true); }}
            className="shrink-0 text-[11px] font-bold text-primary" data-testid={`sop-edit-${item.item_id}`}>Ubah</button>
        )}
      </div>
    </div>
  );
}

/* ---------------------------- accordion section per category ---------------------------- */
export function SOPSection({ title, icon: Icon, templates, whId, canCreate, onChanged, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!templates || templates.length === 0) return null;
  const total = templates.reduce((s, t) => s + t.summary.total, 0);
  const passed = templates.reduce((s, t) => s + t.summary.passed, 0);
  const failed = templates.reduce((s, t) => s + t.summary.failed, 0);
  const allDone = total > 0 && passed === total;
  return (
    <Card className="mb-4 p-0 overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-3 p-4 text-left"
        data-testid={`${testid}-toggle`}>
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${allDone ? "bg-emerald-50 text-emerald-600" : failed > 0 ? "bg-red-50 text-red-600" : "bg-sky-50 text-sky-600"}`}>
          <Icon size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-extrabold text-ink">{title}</h3>
          <p className="text-[11px] text-ink-mute">{templates.map((t) => t.name).join(" · ")}</p>
        </div>
        <Badge tone={allDone ? "success" : failed > 0 ? "danger" : "warn"} data-testid={`${testid}-progress`}>
          {passed}/{total} {failed > 0 && `· ${failed} gagal`}
        </Badge>
        <ChevronDown size={17} className={`shrink-0 text-ink-mute transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-line p-4 pt-3">
          {templates.map((t) => (
            <div key={t.template_key} data-testid={`sop-tpl-${t.template_key}`}>
              {templates.length > 1 && <p className="mb-1.5 text-xs font-extrabold uppercase tracking-wide text-ink-soft">{t.name}</p>}
              <SectionedItems tpl={t} whId={whId} canCreate={canCreate} onChanged={onChanged} />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SectionedItems({ tpl, whId, canCreate, onChanged }) {
  const groups = {};
  for (const it of tpl.items) {
    const k = it.section || "";
    (groups[k] = groups[k] || []).push(it);
  }
  return (
    <div className="space-y-2.5">
      {Object.entries(groups).map(([section, items]) => (
        <div key={section || "_"}>
          {section && <p className="mb-1 flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-ink-mute"><Info size={10} /> {section}</p>}
          <div className="space-y-1.5">
            {items.map((it) => (
              <SopItemRow key={it.item_id} item={it} tplKey={tpl.template_key} whId={whId} canCreate={canCreate} onChanged={onChanged} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
